<template>
    <wrapper-features-templates-operations
    :customBreadcrumbs="customBreadcrumbs"
    :feedbackOperationData="headerOperationData"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :activeStep="activeStep"
    :showStepper="(activeStep <= stepperParams.length)"
    :stepperParams="stepperParams"
    :showFooter="(activeStep <= stepperParams.length) && !showLoader && !applyErrorState"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :scrollValue="scrollValue"
    :tokenTitleAppend="t('savings.products.constitute.tokenTitleAppend')"
    @parentBreadcrumbClicked="navigateByName(routeList.SAVINGS_PRODUCTS_NAME)"
    @clearScrollToValue="scrollValue = null"
	@challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, createDPRequest)"
    @backOrCloseClicked="navigateByName(routeList.SAVINGS_PRODUCTS_NAME)">
        <!-- Insert content -->
        <component
        v-if="!applyErrorState"
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :showStepLoader="showStepLoader"
        :selectedProduct="selectedProduct"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :clearFormErrors="clearFormErrors"
        :currency="currency"
        :accountsBalances="accountsBalanceParsedByProductCurrency"
        :productInterestRates="getProductInterestRates"
        :responseData="responseData"
        :isProductPlafondError="isProductPlafondError"
        @scrollTo="scrollValue = $event"
        @changeSelectedProduct="changeSelectedProductHandler"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @formErrorsCleared="clearFormErrors = false"
        @formDataChange="formDataChangeHandler"
        @showOverlayLoader="showOverlayLoader = !showOverlayLoader"
        @restartProcess="restartProcess"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>

    <loaders-generic
    v-if="showOverlayLoader"
    :isOverlay="showOverlayLoader"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Assets and Helpers */
import { atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { createTermsReceiptURL } from '@/network/api/terms.api.js'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { availableProductsFetch, requestAccountsBalances, requestProductInterestRates, subscribeDPProduct, clearCreateDPStates } = mapActions(moduleMapper.SAVINGS_PRODUCTS)
const { getAccountsBalances, getProductInterestRates } = mapGetters(moduleMapper.SAVINGS_PRODUCTS)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* props received
@selectedProduct { Array } - data received from the chosen product (Savings -> Consult -> Subscribe -> Constitute)
*/
const props = defineProps({
    params: Array
})

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)

/* Data */
const selectedProduct = ref(props.params)
const currency = ref(selectedProduct.value.currency.name)
const applyErrorState = ref(false)

const customBreadcrumbs = {
    currentArea: t('savings.products.constitute.currentArea'),
    parentMenus: [{
        id: 1,
        text: t('savings.products.constitute.breadcrumbs.firstParent')
    }, {
        id: 2,
        text: t('savings.products.constitute.breadcrumbs.secondParent'),
        internalClick: true
    }]
}

/*************************************************
*                                                *
*                   Steps Data                   *
*                                                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('savings.products.constitute.stepper.data') },
    { description: t('savings.products.constitute.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
    const componentName = 'Step' + activeStep.value

    return defineAsyncComponent(() => import('@/components/features/savings/products/constitute/' + componentName))
})

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const scrollValue = ref(null)
const formData = ref({
    productSelected: null,
    accountId: null,
    amount: null,
    acceptConditions: false,
    simulationIsDone: false,
    simulation: null
})

const mandatoryListStep1 = ref([
    'productSelected',
    'amount'
])

const mandatoryListStep2 = ref([
    'acceptConditions'
])

const mandatoryList = computed(() => {
    return activeStep.value === 1 ? mandatoryListStep1.value : mandatoryListStep2.value
})

const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const accountsBalanceParsedByProductCurrency = computed(() => {
    return getAccountsBalances.value.filter(el => el.currency === currency.value)
})

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue

    if (field === 'amount') formData.value.simulationIsDone = false
}

const changeSelectedProductHandler = event => {
    selectedProduct.value = event

    productInterestRatesRequest()
}

/*************************************************
*                                                *
*                Operation Status                *
*                                                *
*************************************************/
const headerOperationData = ref(null)
const responseData = ref()

/*************************************************
*                                                *
*                Initial Requests                *
*                                                *
*************************************************/
const initialRequest = async () => {
	showLoader.value = true
    applyErrorState.value = false
    showFeedbackToast.value = false

    const interestRatesPayload = {
        subProductCode: selectedProduct.value.subProduct.code
    }

	try {
        await availableProductsFetch()
        await requestAccountsBalances()
        await requestProductInterestRates(interestRatesPayload)
        /* Setting Initial Selected Account */
		formData.value.accountId = accountsBalanceParsedByProductCurrency.value.find(account => { return account.default }).id
		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response, initialRequest)
        applyErrorState.value = true
	}
}

/*************************************************
*                                                *
*            Product Interst Requests            *
*                                                *
*************************************************/
const showStepLoader = ref(false)
const productInterestRatesRequest = async () => {
	showStepLoader.value = true

    const payload = {
        subProductCode: selectedProduct.value.subProduct.code
    }

    try {
        await requestProductInterestRates(payload)
        formData.value.productSelected = null
		showStepLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
		showStepLoader.value = false
        errorHandler(error.response, productInterestRatesRequest)
	}
}

/*************************************************
*                Creating DP Request             *
*************************************************/
const createDPRequest = async () => {
	showOverlayLoader.value = true
    showFeedbackToast.value = false

    const payload = {
        accountId: formData.value.accountId,
        amount: formData.value.amount,
        currency: currency.value,
        duration: formData.value.productSelected.maxDuration,
        interest: formData.value.productSelected.interessRate,
        productCode: selectedProduct.value.product.code,
        subProductCode: selectedProduct.value.subProduct.code
    }

	try {
        await subscribeDPProduct(payload)
        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, createDPRequest)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
    activeStep.value++
    responseData.value = data

    headerOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: data.data.hasReceipt ? {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendTermsEmailNotification',
            smsAction: 'sendTermsSMSNotification',
            sendPayload: { operationId: data.data.operationId },
            downloadUrl: createTermsReceiptURL({ params: { operationID: data.data.operationId } }),
            operationId: data.data.operationId
        } : null
    }
}

const errorHandler = (data, retryFn) => {
	showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const validateStep1Inputs = () => {
    const mandatoryListSelected = activeStep.value === 1 ? mandatoryListStep1.value : mandatoryListStep2.value

    if (atLeastOneIsEmpty(formData.value, mandatoryListSelected)) {
        showMandatoryErrors.value = true
        return
    }

    /* Validating Amount */
    const invalidAmount = formData.value.amount === null || formData.value.amount === '' || Number(formData.value.amount) < formData.value.productSelected.minValue

    if (invalidAmount) {
        showMandatoryErrors.value = true
        return
    }

    activeStep.value++
}

const validateStep2Inputs = () => {
    if (!formData.value.acceptConditions) {
        showMandatoryErrors.value = true
        return
    }

    createDPRequest()
}

const clearFormFields = () => {
    /* Clears Form Fields: Amount and Accept Conditions */
    formData.value.amount = null
    formData.value.productSelected = null
    formData.value.simulationIsDone = false
    formData.value.simulation = null
    formData.value.acceptConditions = false

    selectedProduct.value = props.params

    /* Setting Initial Selected Account */
    formData.value.accountId = accountsBalanceParsedByProductCurrency.value.find(account => { return account.default }).id

    clearFormErrors.value = true

    productInterestRatesRequest()
}

/* Footer Buttons Data */
const footerButtonsParams = ref([
    {
        label: t('savings.products.constitute.footerButtons.clear'),
        step: 1,
        buttonPosition: 'left',
        callbackFunction: clearFormFields
    }, {
        label: t('savings.products.constitute.footerButtons.continue'),
        step: 1,
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        callbackFunction: validateStep1Inputs
    }, {
        label: t('savings.products.constitute.footerButtons.back'),
        step: 2,
        icon: 'arrow-left-full',
        callbackFunction: () => activeStep.value--
    }, {
        label: t('savings.products.constitute.footerButtons.validate'),
        step: 2,
        icon: 'check-line',
        buttonType: 'primary',
        iconPosition: 'right',
        callbackFunction: validateStep2Inputs
    }
])

const restartProcess = () => {
    clearFormFields()
    activeStep.value = 1
}

initialRequest()

onBeforeUnmount(() => {
    clearCreateDPStates()
})
</script>

<style lang="scss" scoped>
</style>
